<script lang="ts" setup>
    import Modal from "@/base-components/Dialog/Modal.vue";
    import Button from "@/base-components/Button";
    import BaseButton from "@/base-components/Button/BaseButton.vue";
    import {
        provide,
        reactive,
        ref,
        toRefs,
        defineEmits
    } from "vue";
    import { useVuelidate } from "@vuelidate/core";
    import { helpers, required } from "@vuelidate/validators";
    import { useEmployeesStore } from "@/stores/employeesStore";
    import {
        NotificationElement, BaseNotification
    } from "@/base-components/Notification";
    import Lucide from "@/base-components/Lucide";
    import SubmitButton from "@/base-components/Button/SubmitButton.vue";
    import Tippy from "@/base-components/Tippy/Tippy.vue";

    type ErrorMessages = {
        [key: string]: string[];
    };

    const emit = defineEmits(["fetchEmployees"]);

    const dialog = ref(false);
    const uploading = ref(false);
    const EmployeeStore = useEmployeesStore();
    const backendValidationErrors = ref({});
    const skippedMessage = ref('');
    const errorNotification = ref<NotificationElement>();
    const successNotification = ref<NotificationElement>();
    // @ts-ignore
    const $externalResults = ref<Record<string, any> | []>({});
    const inputFile = ref<HTMLInputElement>();

    const uploadFile = (e: Event) => {
        const target = e.target as HTMLInputElement;

        if (!target.files) {
            return;
        }

        form.attachment = target.files[0];
    };

    const form = reactive({
        attachment: null as File | null
    });

    const rules = {
        attachment: {
            required: helpers.withMessage('Please upload the leave credit import template', required),
        }
    };
    //@ts-ignore
    //prettier-ignore
    const validate = useVuelidate(rules, toRefs(form), {$externalResults});

    const open = () => {
        dialog.value = true;
    };

    const close = () => {
        dialog.value = false;
        $externalResults.value = {};
        skippedMessage.value = '';
    };

    const download = async () => {
        await EmployeeStore.downloadEmployeeLeaveCreditImportTemplate();
    };

    const errorNotificationToggle = () => {
        // Show notification
        errorNotification.value?.showToast();
    };
    provide(
        "bind[errorNotification]",
        (el: NotificationElement) => {
            errorNotification.value = el;
        }
    );
    const showErrorsNotification = () => {
        errorNotificationToggle();
    };

    const successNotificationToggle = () => {
        successNotification.value?.showToast();
    };
    provide(
        "bind[successNotification]",
        (el: NotificationElement) => {
            successNotification.value = el;
        }
    );
    const showSuccessNotification = () => {
        successNotificationToggle();
    };

    const onClickUpload = async () => {
        uploading.value = true;
        skippedMessage.value = '';
        validate.value.$reset();
        validate.value.$clearExternalResults();
        validate.value.$touch();

        if (validate.value.$invalid) {
            backendValidationErrors.value = {
                message: ["Please upload the leave credit import template"]
            };

            showErrorsNotification();
            uploading.value = false;
            return;
        }

        if (!validate.value.$invalid) {
            try {
                const fd = new FormData();
                fd.append(
                    "attachment",
                    form.attachment as File
                );

                const response = await EmployeeStore.importEmployeeLeaveCreditImportTemplate(
                    fd
                );

                if (!Object.hasOwn(response, 'failures')) {
                    close();
                } else {
                    // @ts-ignore
                    inputFile.value.value = null;

                    form.attachment = null;
                    skippedMessage.value = response.failures;
                    validate.value.$reset();
                    validate.value.$clearExternalResults();
                }

                showSuccessNotification();
                emit("fetchEmployees");
            } catch (error: any) {
                // @ts-ignore
                inputFile.value.value = null;
                form.attachment = null;
                validate.value.$reset();
                $externalResults.value =
                    error?.response?.data?.errors || {};

                if (
                    $externalResults.value &&
                    Object.keys($externalResults.value).length >
                        0 &&
                    Object.values(
                        $externalResults.value as ErrorMessages
                    ).some((a: any) =>
                        a[0].includes("Skipping")
                    )
                ) {
                    emit("fetchEmployees");
                }
            } finally {
                uploading.value = false;
            }
        }
    };
</script>

<template>
    <Modal :open="dialog" size="xl">
        <template #trigger>
            <BaseButton
                variant="information"
                kind="icon"
                @click="open"
                icon="CalendarArrowDown"
                tippy-content="Import Leave Credits"
            ></BaseButton>
        </template>

        <template #content>
            <div
                class="mb-4 flex rounded-lg bg-blue-100 p-4 text-sm text-blue-800 dark:bg-gray-800
                    dark:text-blue-400"
                role="alert"
            >
                <svg
                    class="me-3 mt-[2px] inline h-4 w-4 flex-shrink-0"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                >
                    <path
                        d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                    />
                </svg>
                <span class="sr-only">Info</span>
                <div class="ml-2">
                    <span class="font-medium">
                        Please be advised that if the employee does not have the
                        following information or is not active, it will be
                        skipped:
                    </span>
                    <ul class="mt-2 list-inside list-disc">
                        <li>SystemID</li>
                        <li>Employee ID No</li>
                        <li>FullName</li>
                        <li>Leave Type</li>
                        <li>Credits</li>
                    </ul>

                    <div class="mt-4">
                        <span class="font-medium">
                            The following fields should follow the validations
                            below:
                        </span>
                        <ul class="mt-2 list-inside list-disc">
                            <li>
                                System ID must match must the value on our
                                portal
                            </li>
                            <li>
                                Employee ID No must match the value on our
                                portal
                            </li>
                            <li>
                                Leave Type must match the value on our portal
                            </li>
                            <li>Credits must be a number</li>
                        </ul>
                    </div>

                    <div class="mt-2"></div>
                    <div class="mt-2">
                        Thank you for your attention to this matter.
                    </div>
                </div>
            </div>

            <div
                class="flex items-center border-b border-gray-400/50 dark:border-darkmode-400"
            >
                <h2 class="mb-4 mr-auto text-base font-medium">Select File</h2>
            </div>

            <div
                v-if="skippedMessage"
                class="mt-4 rounded bg-yellow-100 px-4 text-yellow-800"
            >
                <p class="p-4 font-semibold">
                    The rows that are using the following leave types were
                    skipped:
                </p>
                <p class="p-4">
                    {{ skippedMessage }}
                </p>
                <p class="p-4">
                    Kindly configure the leave types in the system to match the
                    leave types in the file.
                </p>
            </div>

            <div
                v-if="
                    Object.keys($externalResults).length > 0
                "
                class="mt-4 rounded bg-red-200 px-4 text-red-600"
            >
                <h5 class="pt-4 font-semibold">Something went wrong.</h5>
                <ul class="list-disc p-4">
                    <li v-for="error in $externalResults as ErrorMessages">
                        {{ error[0] }}
                    </li>
                </ul>
            </div>

            <div class="mt-4">
                <p>
                    <input
                        ref="inputFile"
                        id="uploadFileBtn"
                        class="btn mr-2 shadow-md"
                        type="file"
                        @change="uploadFile"
                    />
                </p>
                <template v-if="validate.attachment.$error">
                    <div
                        v-for="(error, index) in validate
                            .attachment.$errors"
                        :key="index"
                        class="mt-2 text-danger"
                    >
                        {{ error.$message }}
                    </div>
                </template>
            </div>
        </template>

        <template #action>
            <div class="flex flex-col justify-between gap-2 sm:flex-row">
                <div>
                    <Button
                        class="w-full sm:w-auto"
                        type="button"
                        variant="outline-secondary"
                        @click="download"
                    >
                        Download All Employees Leave Credits
                    </Button>
                </div>

                <div class="flex flex-col justify-end gap-2 sm:flex-row">
                    <Button
                        class="w-full sm:w-24"
                        type="button"
                        variant="outline-secondary"
                        @click="close"
                    >
                        Close
                    </Button>
                    <SubmitButton
                        id="btn_process"
                        class="w-full sm:w-32"
                        variant="primary"
                        processing-label="Importing..."
                        :is-processing="uploading"
                        @click="onClickUpload"
                    >
                        Upload
                    </SubmitButton>
                </div>
            </div>
        </template>
    </Modal>

    <BaseNotification refKey="successNotification">
        Employee invoice information has been imported successfully.
    </BaseNotification>

    <BaseNotification type="error" refKey="errorNotification">
        <ul class="list-inside list-disc">
            <template v-for="errorBag in backendValidationErrors">
                <li>
                    {{ errorBag[0] }}
                </li>
            </template>
        </ul>
    </BaseNotification>
</template>
